'use client';

import type React from 'react';
import {
  loadItemFromSessionStorage,
  removeItemFromSessionStorage,
  saveItemToSessionStorage
} from '~/utilities/sessionStorage';

export const SPA_NAV_START_MARK = 'spa-navigation-start';
export const SRP_TILE_CLICK_STORAGE_KEY = 'srp-tile-click-timing';
export const SRP_TILE_CLICK_MAX_AGE_MS = 60_000;
export const SRP_SCROLL_RESTORE_STORAGE_KEY = 'srp-scroll-restore-tile';
export const SRP_SCROLL_RESTORE_MAX_AGE_MS = 60 * 60 * 1000;

// PerformanceNavigation.type enum: 0 navigate, 1 reload, 2 back_forward.
const LEGACY_TYPE_BACK_FORWARD = 2;

export type NavType = 'back_forward' | 'navigate';

// Nav type of the most recent SPA transition. Only read behind the
// isSpaNavigation() mark guard, so a stale value can't leak into a doc load.
let lastSpaNavType: NavType = 'navigate';

export type SrpTileClickStamp = {
  timestamp: number;
  vehicleId: number;
};

export type ScrollRestoreMissReason = 'malformed' | 'no-stamp' | 'stale';

export type ScrollRestoreConsumeResult =
  | { reason: ScrollRestoreMissReason }
  | { vehicleId: number };

type TileClickEvent = Pick<
  React.MouseEvent,
  'altKey' | 'button' | 'ctrlKey' | 'metaKey' | 'shiftKey'
>;

/**
 * Stamps a performance mark at the moment of SPA navigation intent (link click or popstate),
 * and records the nav type for that transition. Clears any previous mark so only the most
 * recent navigation is tracked.
 */
export function markSpaNavigationStart(navType: NavType = 'navigate'): void {
  lastSpaNavType = navType;
  performance.clearMarks(SPA_NAV_START_MARK);
  performance.mark(SPA_NAV_START_MARK);
}

/**
 * Raw `PerformanceNavigationTiming.type` for the current document load
 * (`navigate` | `reload` | `back_forward` | `prerender`), or `undefined` when
 * the Navigation Timing entry is unavailable.
 */
export function getPerformanceNavigationType(): string | undefined {
  const [entry] = performance.getEntriesByType('navigation');
  if (!entry) {
    return undefined;
  }

  // `type` is on the PerformanceNavigationTiming subtype; read it defensively
  // rather than casting to a type the runtime may not implement (older jsdom).
  const type = Reflect.get(entry, 'type');

  return typeof type === 'string' ? type : undefined;
}

/**
 * Whether the current *document* load was a back/forward traversal. Falls back to
 * the deprecated `performance.navigation.type` because Safari sometimes reports the
 * modern type as `navigate` on a back/forward reload but still sets the legacy one.
 */
export function isDocBackForwardNavigation(): boolean {
  if (getPerformanceNavigationType() === 'back_forward') {
    return true;
  }

  const legacy = Reflect.get(performance, 'navigation');

  return legacy?.type === LEGACY_TYPE_BACK_FORWARD;
}

/**
 * SPA-aware drop-in replacement for `Math.round(performance.now())`.
 *
 * Returns rounded elapsed milliseconds since the navigation that brought the user to the
 * current page. For SPA navigations, reads the `spa-navigation-start` performance mark
 * stamped at link-click or popstate. For full page loads, no mark exists and
 * `performance.now()` is returned directly — correct because `performance.timeOrigin`
 * was reset by the browser at load time.
 */
export function isSpaNavigation(): boolean {
  return performance.getEntriesByName(SPA_NAV_START_MARK, 'mark').length > 0;
}

export function navPerformanceNow(): number {
  const marks = performance.getEntriesByName(SPA_NAV_START_MARK, 'mark');
  if (marks.length > 0) {
    return Math.round(
      Math.max(0, performance.now() - marks[marks.length - 1].startTime)
    );
  }

  return Math.round(performance.now());
}

/**
 * Nav type for the `load-time` event: the stamped SPA type for SPA transitions,
 * else the document-load resolver. (bfcache restores never re-run JS, so that
 * back/forward is emitted separately from a `pageshow` handler.)
 */
export function getLoadNavType(): NavType {
  if (isSpaNavigation()) {
    return lastSpaNavType;
  }

  return isDocBackForwardNavigation() ? 'back_forward' : 'navigate';
}

/**
 * Modifier and non-primary-button clicks (new tab/window, download) never
 * navigate the current tab, so a stamp for them could only go stale.
 */
function isTrackableClick(e: TileClickEvent): boolean {
  return !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0;
}

/**
 * Stores a wall-clock timestamp when an SRP vehicle tile is clicked, for
 * auditing load time on the destination VDP. Uses sessionStorage rather than
 * a performance mark so the stamp survives hard (document) navigations in the
 * control experience, not just SPA transitions.
 */
export function markSrpTileClick(vehicleId: number, e: TileClickEvent): void {
  if (!isTrackableClick(e)) return;

  saveItemToSessionStorage(SRP_TILE_CLICK_STORAGE_KEY, {
    timestamp: Date.now(),
    vehicleId
  });
}

/**
 * Stores which vehicle tile was clicked so a later, non-bfcache return to SRP
 * can scroll back to it. Separate from the stamp above: that one is VDP-side,
 * one-shot, and deleted on VDP mount, well before a return trip could read it.
 */
export function markScrollRestoreTarget(
  vehicleId: number,
  e: TileClickEvent
): void {
  if (!isTrackableClick(e)) return;

  saveItemToSessionStorage(SRP_SCROLL_RESTORE_STORAGE_KEY, {
    timestamp: Date.now(),
    vehicleId
  });
}

/**
 * Reads and clears the scroll-restore stamp. Always clears on read (one-shot),
 * regardless of whether the stamp is used, stale, or malformed. Returns a
 * discriminated result so the caller can report *why* a miss happened, not
 * just that one did.
 */
export function consumeScrollRestoreTarget(): ScrollRestoreConsumeResult {
  const stamp = loadItemFromSessionStorage<SrpTileClickStamp>(
    SRP_SCROLL_RESTORE_STORAGE_KEY
  );
  removeItemFromSessionStorage(SRP_SCROLL_RESTORE_STORAGE_KEY);

  if (!stamp) return { reason: 'no-stamp' };
  if (
    typeof stamp.timestamp !== 'number' ||
    typeof stamp.vehicleId !== 'number'
  ) {
    return { reason: 'malformed' };
  }

  const age = Date.now() - stamp.timestamp;
  if (age < 0 || age > SRP_SCROLL_RESTORE_MAX_AGE_MS)
    return { reason: 'stale' };

  return { vehicleId: stamp.vehicleId };
}

export function clearScrollRestoreTarget(): void {
  removeItemFromSessionStorage(SRP_SCROLL_RESTORE_STORAGE_KEY);
}

/**
 * Returns elapsed milliseconds since the SRP tile click stamp, or `undefined`
 * when the current page was not reached via a tile click. Clears the stamp on
 * read so one click produces at most one measurement, and only honors it when
 * the current URL is the VDP for the clicked vehicle and the stamp is fresh —
 * an old stamp means the click never completed a navigation (aborted load,
 * VDP error page), not a real load time.
 */
export function consumeSrpTileClickElapsed(): number | undefined {
  const [, section, vehicleId] = window.location.pathname.split('/');
  if (section !== 'vehicle') {
    return undefined;
  }

  const stamp = loadItemFromSessionStorage<SrpTileClickStamp>(
    SRP_TILE_CLICK_STORAGE_KEY
  );
  if (!stamp) {
    return undefined;
  }

  removeItemFromSessionStorage(SRP_TILE_CLICK_STORAGE_KEY);

  if (
    typeof stamp.timestamp !== 'number' ||
    typeof stamp.vehicleId !== 'number'
  ) {
    return undefined;
  }

  if (vehicleId !== String(stamp.vehicleId)) {
    return undefined;
  }

  const elapsed = Date.now() - stamp.timestamp;
  if (elapsed < 0 || elapsed > SRP_TILE_CLICK_MAX_AGE_MS) {
    return undefined;
  }

  return elapsed;
}
